/**
 * `<HelpdeskSettingsPanel>` — the workspace helpdesk settings, rendered as the
 * "Helpdesk" tab inside the Workspace settings page. Shell ported verbatim from
 * the storybook `_helpdesk-admin.tsx` `HelpdeskSettingsView` + `SectionShell`:
 * a rounded bg-card container, a w-56 section rail (Bold icon when active), and
 * per-section content — icon header, dashed dividers between groups, and a
 * right-aligned Reset / Save changes footer.
 *
 * Sections are scoped to what exists today:
 *   - Transparency / Join announcement / Resolve behavior / CSAT & surveys —
 *     knobs backed by `GET/PATCH /api/handoff-settings` (one shared draft +
 *     save); the CSAT tiles read `GET /api/csat/stats`.
 *   - Routing & assignment — persisted on `handoff-settings.routing` (teams-
 *     routing LLD §4). Strategy + capacity are enforced by the inline
 *     assignment engine; after-hours + reassign save but wait on the
 *     timer-based slice. Also hosts the Support agents roster pointer.
 *
 * Teams (routing groups) moved to the desk sidebar: `/helpdesk/teams`,
 * next to the desk's Members + Roles pages (2026-07-15).
 *
 * The remaining storybook sections (SLA, order processes, automations,
 * macros, tags) stay omitted until they grow a backend.
 *
 * Gated `handoff:configure`; the parent tab only renders when the ability holds.
 */
import { useAbility, useWorkspace } from "@cometchat-ai/auth/client";
import {
  Button,
  Calendar,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  toast,
} from "@cometchat-ai/ui-dashboard";
/** Structurally identical to react-day-picker's `DateRange` (the dashboard
 *  doesn't depend on it directly — the Calendar comes from ui-dashboard). */
type DateRange = { from: Date | undefined; to?: Date | undefined };
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Children, Fragment, useEffect, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { ArrowLeftRight, CheckCircle2, Eye, Smile, Volume2 } from "./icons";
import type { LucideIcon } from "./icons";
import { ErrorState } from "./error-state";
import { ListSkeleton } from "./list-skeleton";
import { formatApiError } from "../lib/format-error";
import {
  csatKeys,
  getCsatStats,
  getHandoffSettings,
  handoffSettingsKeys,
  updateHandoffSettings,
  type CsatStatsRange,
  type HandoffSettings,
} from "../handoff/api";

type CsatWindow = "30d" | "60d" | "90d" | "custom";

// LOCAL calendar day (not toISOString, which shifts e.g. July 1 00:00 IST
// back to June 30 UTC — the picked date must render/query as picked).
const toIsoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

type SectionId = "transparency" | "joinAnnouncement" | "resolveBehavior" | "csat" | "routing";

const SECTIONS: ReadonlyArray<{ id: SectionId; icon: LucideIcon }> = [
  { id: "transparency", icon: Eye },
  { id: "joinAnnouncement", icon: Volume2 },
  { id: "resolveBehavior", icon: CheckCircle2 },
  { id: "csat", icon: Smile },
  { id: "routing", icon: ArrowLeftRight },
];

/** Storybook `SectionShell`: p-5 body, icon header (with an optional
 *  right-aligned `action` — e.g. the CSAT range picker), dashed dividers
 *  between groups, solid divider, then a right-aligned footer. */
function SectionShell({
  icon: Icon,
  title,
  description,
  action,
  footer,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const groups = Children.toArray(children);
  return (
    <div className="p-5">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0 text-foreground" aria-hidden="true" />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold tracking-wide-sm text-foreground">
          {title}
        </h2>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <p className="mt-1 text-2sm text-muted-foreground">{description}</p>
      <div className="mt-6">
        {groups.map((child, i) => (
          <Fragment key={i}>
            {i > 0 ? <div className="-mx-5 my-6 border-t border-dashed border-border" /> : null}
            {child}
          </Fragment>
        ))}
      </div>
      {footer ? (
        <>
          <div className="-mx-5 mt-8 border-t border-border" />
          <div className="mt-6 flex justify-end gap-2">{footer}</div>
        </>
      ) : null}
    </div>
  );
}

/** Storybook `Card`: a group inside a section — small bold title + hint. */
function Group({
  title,
  desc,
  action,
  children,
}: {
  title?: string;
  desc?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      {title ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-2sm font-semibold text-foreground">{title}</h3>
            {desc ? <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p> : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function HelpdeskSettingsPanel() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const ability = useAbility();
  const canConfigure = ability.can("configure", "HandoffEvent");
  const queryClient = useQueryClient();

  const [section, setSection] = useState<SectionId>("transparency");

  const settingsQ = useQuery({
    queryKey: handoffSettingsKeys.get(slug),
    queryFn: () => getHandoffSettings(),
    enabled: canConfigure,
  });
  // CSAT calculation window: preset 30/60/90 days or a custom calendar range.
  const [csatWindow, setCsatWindow] = useState<CsatWindow>("30d");
  const [customRange, setCustomRange] = useState<DateRange | undefined>(undefined);
  const statsRange: CsatStatsRange | null =
    csatWindow !== "custom"
      ? { window: csatWindow }
      : customRange?.from && customRange?.to
        ? { from: toIsoDay(customRange.from), to: toIsoDay(customRange.to) }
        : null;
  const statsQ = useQuery({
    queryKey: csatKeys.stats(slug, statsRange ?? { window: "unset" }),
    queryFn: () => getCsatStats(statsRange!),
    // A custom window waits for a complete from+to selection before fetching.
    enabled: canConfigure && section === "csat" && statsRange !== null,
  });

  const [draft, setDraft] = useState<HandoffSettings | null>(null);
  // Seed the draft ONCE — a background refetch (window focus etc.) must never
  // clobber in-progress edits: resetting `csat.enabled` mid-typing disables the
  // survey inputs, which ejects the cursor from the focused field.
  useEffect(() => {
    if (settingsQ.data) setDraft((d) => d ?? settingsQ.data);
  }, [settingsQ.data]);

  const save = useMutation({
    // Whitespace-only follow-up normalizes to null here (not per keystroke);
    // blanked thumb labels fall back to their defaults.
    mutationFn: (body: HandoffSettings) =>
      updateHandoffSettings({
        ...body,
        csat: {
          ...body.csat,
          followUp: body.csat.followUp?.trim() ? body.csat.followUp : null,
          thumbsUpLabel: body.csat.thumbsUpLabel.trim() || "Good",
          thumbsDownLabel: body.csat.thumbsDownLabel.trim() || "Bad",
        },
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(handoffSettingsKeys.get(slug), next);
      setDraft(next);
      toast.success(t("helpdeskSettings.saved"));
    },
    onError: (e) =>
      toast.error(t("helpdeskSettings.saveFailed"), { description: formatApiError(e) }),
  });

  const dirty =
    draft != null &&
    settingsQ.data != null &&
    (draft.transparency !== settingsQ.data.transparency ||
      draft.resolveBehavior !== settingsQ.data.resolveBehavior ||
      draft.joinAnnouncement.enabled !== settingsQ.data.joinAnnouncement.enabled ||
      draft.joinAnnouncement.template !== settingsQ.data.joinAnnouncement.template ||
      draft.handbackAnnouncement.enabled !== settingsQ.data.handbackAnnouncement.enabled ||
      draft.handbackAnnouncement.template !== settingsQ.data.handbackAnnouncement.template ||
      draft.routing.autoAssignStrategy !== settingsQ.data.routing.autoAssignStrategy ||
      draft.routing.maxOpenPerAgent !== settingsQ.data.routing.maxOpenPerAgent ||
      draft.routing.stopAtCapacity !== settingsQ.data.routing.stopAtCapacity ||
      draft.csat.enabled !== settingsQ.data.csat.enabled ||
      draft.csat.scale !== settingsQ.data.csat.scale ||
      draft.csat.question !== settingsQ.data.csat.question ||
      draft.csat.followUp !== settingsQ.data.csat.followUp ||
      draft.csat.starsHappyFrom !== settingsQ.data.csat.starsHappyFrom ||
      draft.csat.thumbsUpLabel !== settingsQ.data.csat.thumbsUpLabel ||
      draft.csat.thumbsDownLabel !== settingsQ.data.csat.thumbsDownLabel);

  if (!canConfigure) {
    return (
      <p className="px-6 py-12 text-center text-2sm text-muted-foreground">
        {t("helpdeskSettings.forbidden")}
      </p>
    );
  }

  // The shared draft footer (storybook shows Reset + Save on every section).
  const footer = (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="h-9 rounded-lg px-4"
        disabled={!dirty || save.isPending}
        onClick={() => settingsQ.data && setDraft(settingsQ.data)}
      >
        {t("helpdeskSettings.reset")}
      </Button>
      <Button
        type="button"
        size="sm"
        className="h-9 rounded-lg px-4"
        disabled={!dirty || save.isPending}
        onClick={() => draft && save.mutate(draft)}
        data-testid="helpdesk-settings-save"
      >
        {t("helpdeskSettings.save")}
      </Button>
    </>
  );

  return (
    <div
      className="flex h-full min-h-[60vh] w-full overflow-hidden rounded-xl border border-border bg-card"
      data-testid="settings-helpdesk"
    >
      <nav
        className="flex w-56 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-border bg-muted/30 p-2"
        aria-label={t("helpdeskSettings.title")}
      >
        {SECTIONS.map(({ id, icon: Icon }) => {
          const isActive = section === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              data-active={isActive}
              data-testid={`helpdesk-section-${id}`}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-2sm tracking-wide-sm transition-colors",
                isActive
                  ? "bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] font-medium text-foreground"
                  : "text-[color:var(--color-fg-muted)] hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] hover:text-foreground",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  isActive
                    ? "text-[color:color-mix(in_oklch,var(--color-fg)_55%,transparent)]"
                    : "text-[color:var(--color-fg-muted)]",
                )}
                variant={isActive ? "Bold" : undefined}
              />
              {t(`helpdeskSettings.sections.${id}`)}
            </button>
          );
        })}
      </nav>

      <div className="min-h-0 min-w-0 flex-1 overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {settingsQ.isPending || !draft ? (
          <div className="p-5">
            <ListSkeleton rows={4} itemClassName="h-10 w-full" className="space-y-3" />
          </div>
        ) : settingsQ.isError ? (
          <div className="p-5">
            <ErrorState message={formatApiError(settingsQ.error)} onRetry={settingsQ.refetch} />
          </div>
        ) : section === "transparency" ? (
          <SectionShell
            icon={Eye}
            title={t("helpdeskSettings.transparency.title")}
            description={t("helpdeskSettings.transparency.description")}
            footer={footer}
          >
            <RadioGroup
              value={draft.transparency}
              onValueChange={(v) =>
                setDraft((d) => (d ? { ...d, transparency: v as "badge" | "silent" } : d))
              }
              className="gap-2"
            >
              {(["badge", "silent"] as const).map((opt) => (
                <Label
                  key={opt}
                  htmlFor={`transparency-${opt}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-2sm has-[:checked]:border-foreground"
                >
                  <RadioGroupItem value={opt} id={`transparency-${opt}`} className="mt-0.5" />
                  <span>
                    <span className="font-medium text-foreground">
                      {t(`helpdeskSettings.transparency.${opt}.label`)}
                    </span>
                    <span className="mt-0.5 block text-muted-foreground">
                      {t(`helpdeskSettings.transparency.${opt}.hint`)}
                    </span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </SectionShell>
        ) : section === "resolveBehavior" ? (
          <SectionShell
            icon={CheckCircle2}
            title={t("helpdeskSettings.resolveBehavior.title")}
            description={t("helpdeskSettings.resolveBehavior.description")}
            footer={footer}
          >
            <RadioGroup
              value={draft.resolveBehavior}
              onValueChange={(v) =>
                setDraft((d) => (d ? { ...d, resolveBehavior: v as "reopen" | "terminal" } : d))
              }
              className="gap-2"
            >
              {(["reopen", "terminal"] as const).map((opt) => (
                <Label
                  key={opt}
                  htmlFor={`resolve-${opt}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-2sm has-[:checked]:border-foreground"
                >
                  <RadioGroupItem value={opt} id={`resolve-${opt}`} className="mt-0.5" />
                  <span>
                    <span className="font-medium text-foreground">
                      {t(`helpdeskSettings.resolveBehavior.${opt}.label`)}
                    </span>
                    <span className="mt-0.5 block text-muted-foreground">
                      {t(`helpdeskSettings.resolveBehavior.${opt}.hint`)}
                    </span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </SectionShell>
        ) : section === "csat" ? (
          <SectionShell
            icon={Smile}
            title={t("helpdeskSettings.csat.title")}
            description={t("helpdeskSettings.csat.description")}
            action={
              <div className="flex items-center gap-1.5" data-testid="csat-window-picker">
                <Select value={csatWindow} onValueChange={(v) => setCsatWindow(v as CsatWindow)}>
                  <SelectTrigger className="h-8 w-36 text-2sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="30d">{t("helpdeskSettings.csat.window.30d")}</SelectItem>
                    <SelectItem value="60d">{t("helpdeskSettings.csat.window.60d")}</SelectItem>
                    <SelectItem value="90d">{t("helpdeskSettings.csat.window.90d")}</SelectItem>
                    <SelectItem value="custom">
                      {t("helpdeskSettings.csat.window.custom")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {csatWindow === "custom" ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-2sm font-normal"
                        data-testid="csat-custom-range"
                      >
                        {customRange?.from && customRange?.to
                          ? `${toIsoDay(customRange.from)} → ${toIsoDay(customRange.to)}`
                          : t("helpdeskSettings.csat.window.pickRange")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto p-0">
                      <Calendar
                        mode="range"
                        numberOfMonths={2}
                        selected={customRange}
                        onSelect={setCustomRange}
                        disabled={{ after: new Date() }}
                      />
                    </PopoverContent>
                  </Popover>
                ) : null}
              </div>
            }
            footer={footer}
          >
            {/* Stat tiles over the selected window — storybook CsatSection grid. */}
            <div className="grid grid-cols-3 gap-3" data-testid="csat-stats">
              {(
                [
                  {
                    key: "csatPct",
                    label: t("helpdeskSettings.csat.stats.csat", {
                      window:
                        csatWindow === "custom"
                          ? t("helpdeskSettings.csat.window.customShort")
                          : csatWindow,
                    }),
                    value: statsQ.data?.csatPct != null ? `${statsQ.data.csatPct}%` : "—",
                  },
                  {
                    key: "responseRate",
                    label: t("helpdeskSettings.csat.stats.responseRate"),
                    value: statsQ.data?.responseRate != null ? `${statsQ.data.responseRate}%` : "—",
                  },
                  {
                    key: "responded",
                    label: t("helpdeskSettings.csat.stats.responses"),
                    value: statsQ.data ? statsQ.data.responded.toLocaleString() : "—",
                  },
                ] as const
              ).map((s) => (
                <div key={s.key} className="rounded-lg border border-border p-4">
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                  <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            <Group title={t("helpdeskSettings.csat.surveyGroup")}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-2sm font-medium text-foreground">
                    {t("helpdeskSettings.csat.sendLabel")}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t("helpdeskSettings.csat.sendHint")}
                  </div>
                </div>
                <Switch
                  checked={draft.csat.enabled}
                  onCheckedChange={(enabled) =>
                    setDraft((d) => (d ? { ...d, csat: { ...d.csat, enabled } } : d))
                  }
                  data-testid="csat-enable-toggle"
                  aria-label={t("helpdeskSettings.csat.sendLabel")}
                />
              </div>
              <div className={cn("mt-5", !draft.csat.enabled && "opacity-50")}>
                <div className="text-2sm font-medium text-foreground">
                  {t("helpdeskSettings.csat.scaleLabel")}
                </div>
                <RadioGroup
                  value={draft.csat.scale}
                  onValueChange={(v) =>
                    setDraft((d) =>
                      d ? { ...d, csat: { ...d.csat, scale: v as "stars" | "thumbs" } } : d,
                    )
                  }
                  disabled={!draft.csat.enabled}
                  className="mt-2 flex flex-wrap gap-2"
                >
                  {(["stars", "thumbs"] as const).map((opt) => (
                    <Label
                      key={opt}
                      htmlFor={`csat-scale-${opt}`}
                      className={cn(
                        "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-2sm transition-colors",
                        draft.csat.scale === opt
                          ? "border-[color:var(--color-fg)] text-foreground"
                          : "border-border text-muted-foreground hover:bg-muted/50",
                      )}
                    >
                      <RadioGroupItem value={opt} id={`csat-scale-${opt}`} />
                      {t(`helpdeskSettings.csat.scales.${opt}`)}
                    </Label>
                  ))}
                </RadioGroup>

                {draft.csat.scale === "stars" ? (
                  <>
                    <div className="mt-4 text-2sm font-medium text-foreground">
                      {t("helpdeskSettings.csat.happyFrom.label")}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t("helpdeskSettings.csat.happyFrom.hint")}
                    </p>
                    <Select
                      value={String(draft.csat.starsHappyFrom)}
                      onValueChange={(v) =>
                        setDraft((d) =>
                          d ? { ...d, csat: { ...d.csat, starsHappyFrom: Number(v) } } : d,
                        )
                      }
                      disabled={!draft.csat.enabled}
                    >
                      <SelectTrigger className="mt-1.5 w-56 text-2sm" data-testid="csat-happy-from">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {([3, 4, 5] as const).map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {t(`helpdeskSettings.csat.happyFrom.${n}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-2sm font-medium text-foreground">
                        {t("helpdeskSettings.csat.thumbsUpLabel")}
                      </div>
                      <Input
                        value={draft.csat.thumbsUpLabel}
                        disabled={!draft.csat.enabled}
                        maxLength={40}
                        onChange={(e) =>
                          setDraft((d) =>
                            d ? { ...d, csat: { ...d.csat, thumbsUpLabel: e.target.value } } : d,
                          )
                        }
                        className="mt-1.5 text-2sm"
                        data-testid="csat-thumbs-up-label"
                      />
                    </div>
                    <div>
                      <div className="text-2sm font-medium text-foreground">
                        {t("helpdeskSettings.csat.thumbsDownLabel")}
                      </div>
                      <Input
                        value={draft.csat.thumbsDownLabel}
                        disabled={!draft.csat.enabled}
                        maxLength={40}
                        onChange={(e) =>
                          setDraft((d) =>
                            d ? { ...d, csat: { ...d.csat, thumbsDownLabel: e.target.value } } : d,
                          )
                        }
                        className="mt-1.5 text-2sm"
                        data-testid="csat-thumbs-down-label"
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 text-2sm font-medium text-foreground">
                  {t("helpdeskSettings.csat.questionLabel")}
                </div>
                <Input
                  value={draft.csat.question}
                  disabled={!draft.csat.enabled}
                  maxLength={300}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, csat: { ...d.csat, question: e.target.value } } : d,
                    )
                  }
                  className="mt-1.5 text-2sm"
                  data-testid="csat-question"
                />
                <div className="mt-3 text-2sm font-medium text-foreground">
                  {t("helpdeskSettings.csat.followUpLabel")}
                </div>
                <Input
                  value={draft.csat.followUp ?? ""}
                  disabled={!draft.csat.enabled}
                  maxLength={300}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            // Keep the raw string while typing (trimming per
                            // keystroke eats leading spaces); whitespace-only
                            // normalizes to null at save time.
                            csat: { ...d.csat, followUp: e.target.value || null },
                          }
                        : d,
                    )
                  }
                  className="mt-1.5 text-2sm"
                  data-testid="csat-follow-up"
                />
              </div>
            </Group>
          </SectionShell>
        ) : section === "routing" ? (
          <RoutingSection slug={slug} draft={draft} setDraft={setDraft} footer={footer} />
        ) : (
          <SectionShell
            icon={Volume2}
            title={t("helpdeskSettings.announcements.title")}
            description={t("helpdeskSettings.announcements.description")}
            footer={footer}
          >
            <Group
              title={t("helpdeskSettings.joinAnnouncement.title")}
              desc={t("helpdeskSettings.joinAnnouncement.description")}
              action={
                <Switch
                  checked={draft.joinAnnouncement.enabled}
                  onCheckedChange={(enabled) =>
                    setDraft((d) =>
                      d ? { ...d, joinAnnouncement: { ...d.joinAnnouncement, enabled } } : d,
                    )
                  }
                  data-testid="helpdesk-join-toggle"
                  aria-label={t("helpdeskSettings.joinAnnouncement.title")}
                />
              }
            >
              <div className="space-y-1.5">
                <Label htmlFor="join-template" className="text-2sm text-muted-foreground">
                  {t("helpdeskSettings.joinAnnouncement.templateLabel")}
                </Label>
                <Input
                  id="join-template"
                  value={draft.joinAnnouncement.template}
                  disabled={!draft.joinAnnouncement.enabled}
                  maxLength={200}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            joinAnnouncement: { ...d.joinAnnouncement, template: e.target.value },
                          }
                        : d,
                    )
                  }
                  className="text-2sm"
                  data-testid="helpdesk-join-template"
                />
                <p className="text-2xs text-[color:var(--color-fg-subtle)]">
                  {t("helpdeskSettings.joinAnnouncement.templateHint")}
                </p>
              </div>
            </Group>

            <Group
              title={t("helpdeskSettings.handbackAnnouncement.title")}
              desc={t("helpdeskSettings.handbackAnnouncement.description")}
              action={
                <Switch
                  checked={draft.handbackAnnouncement.enabled}
                  onCheckedChange={(enabled) =>
                    setDraft((d) =>
                      d
                        ? { ...d, handbackAnnouncement: { ...d.handbackAnnouncement, enabled } }
                        : d,
                    )
                  }
                  data-testid="helpdesk-handback-toggle"
                  aria-label={t("helpdeskSettings.handbackAnnouncement.title")}
                />
              }
            >
              <div className="space-y-1.5">
                <Label htmlFor="handback-template" className="text-2sm text-muted-foreground">
                  {t("helpdeskSettings.handbackAnnouncement.templateLabel")}
                </Label>
                <Input
                  id="handback-template"
                  value={draft.handbackAnnouncement.template}
                  disabled={!draft.handbackAnnouncement.enabled}
                  maxLength={200}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            handbackAnnouncement: {
                              ...d.handbackAnnouncement,
                              template: e.target.value,
                            },
                          }
                        : d,
                    )
                  }
                  className="text-2sm"
                  data-testid="helpdesk-handback-template"
                />
                <p className="text-2xs text-[color:var(--color-fg-subtle)]">
                  {t("helpdeskSettings.handbackAnnouncement.templateHint")}
                </p>
              </div>
            </Group>
          </SectionShell>
        )}
      </div>
    </div>
  );
}

/**
 * Routing & assignment — persisted on `handoff-settings.routing`, edited via
 * the panel's shared draft (same Reset/Save footer as every other section).
 * Strategy + capacity drive the inline assignment engine today; after-hours
 * and reassign save but are enforced by the later timer-based slice (the
 * inline note says so). Also hosts the Support agents roster pointer.
 */
function RoutingSection({
  slug,
  draft,
  setDraft,
  footer,
}: {
  slug: string;
  draft: HandoffSettings;
  setDraft: Dispatch<SetStateAction<HandoffSettings | null>>;
  footer: ReactNode;
}) {
  const { t } = useTranslation("workspace");
  const patchRouting = (patch: Partial<HandoffSettings["routing"]>) =>
    setDraft((d) => (d ? { ...d, routing: { ...d.routing, ...patch } } : d));

  return (
    <SectionShell
      icon={ArrowLeftRight}
      title={t("helpdeskSettings.routing.title")}
      description={t("helpdeskSettings.routing.description")}
      footer={footer}
    >
      <div data-testid="helpdesk-routing-section">
        <p className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-2sm text-muted-foreground">
          {t("helpdeskSettings.routing.enforcementNotice")}
        </p>
      </div>

      <Group title={t("helpdeskSettings.routing.strategy.title")}>
        <RadioGroup
          value={draft.routing.autoAssignStrategy}
          onValueChange={(v) =>
            patchRouting({
              autoAssignStrategy: v as HandoffSettings["routing"]["autoAssignStrategy"],
            })
          }
          className="gap-2"
        >
          {(["round_robin", "balanced_load", "claim"] as const).map((opt) => (
            <Label
              key={opt}
              htmlFor={`routing-${opt}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-2sm has-[:checked]:border-foreground"
            >
              <RadioGroupItem value={opt} id={`routing-${opt}`} className="mt-0.5" />
              <span>
                <span className="font-medium text-foreground">
                  {t(`helpdeskSettings.routing.strategy.${opt}.label`)}
                </span>
                <span className="mt-0.5 block text-muted-foreground">
                  {t(`helpdeskSettings.routing.strategy.${opt}.hint`)}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      </Group>

      <Group title={t("helpdeskSettings.routing.capacity.title")}>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-2sm font-medium text-foreground">
                {t("helpdeskSettings.routing.capacity.maxPerAgent.label")}
              </div>
              <div className="text-2sm text-muted-foreground">
                {t("helpdeskSettings.routing.capacity.maxPerAgent.hint")}
              </div>
            </div>
            <div className="inline-flex items-center rounded-md border border-border">
              <button
                type="button"
                onClick={() =>
                  patchRouting({ maxOpenPerAgent: Math.max(1, draft.routing.maxOpenPerAgent - 1) })
                }
                className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-muted"
                aria-label="decrease"
              >
                −
              </button>
              <span className="w-8 text-center text-2sm font-medium tabular-nums text-foreground">
                {draft.routing.maxOpenPerAgent}
              </span>
              <button
                type="button"
                onClick={() =>
                  patchRouting({
                    maxOpenPerAgent: Math.min(100, draft.routing.maxOpenPerAgent + 1),
                  })
                }
                className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-muted"
                aria-label="increase"
              >
                +
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-2sm font-medium text-foreground">
                {t("helpdeskSettings.routing.capacity.stopAtCapacity.label")}
              </div>
              <div className="text-2sm text-muted-foreground">
                {t("helpdeskSettings.routing.capacity.stopAtCapacity.hint")}
              </div>
            </div>
            <Switch
              checked={draft.routing.stopAtCapacity}
              onCheckedChange={(stopAtCapacity) => patchRouting({ stopAtCapacity })}
              aria-label={t("helpdeskSettings.routing.capacity.stopAtCapacity.label")}
            />
          </div>
        </div>
      </Group>

      <Group
        title={t("helpdeskSettings.roster.title")}
        desc={t("helpdeskSettings.roster.description")}
      >
        <Button asChild variant="outline" size="sm">
          <Link to={`/w/${slug}/support-agents`}>{t("helpdeskSettings.roster.manage")}</Link>
        </Button>
      </Group>
    </SectionShell>
  );
}
